// QV-4241 Dual Price / NCA — faithful port of the Android OrderCreateViewModel conversion helpers
// (surchargeMultiplier · toBasePrice · getBaseSubTotal · getBaseOrderDiscount · getBaseTaxAmount ·
// getBaseGrandTotalFromBasePrice · getCard* · getCreditPriceNca · perTaxValueString).
//
// Model (Android master, final = PROPORTIONAL): in mode "2" every catalog/custom price in the cart is
// the CARD (credit) price = round2(base × M), M = 1 + pct/100. All cart math runs in credit space;
// the CASH (base) side is derived here. Mode "1" (NCA) keeps base prices and adds a surcharge on the
// card tender at payment time. Mode "0" is off. The fixed-dollar correction terms Android still
// carries (itemDollarOffCorrection, dollarOffBaseCorrection, taxDollarOffCorrection) are all 0 on
// master and are omitted.

import { roundHalfUp, toNum } from "./money";

export const SURCHARGE_OFF = "0";
export const SURCHARGE_NCA = "1";
export const SURCHARGE_DUAL = "2";

const r2 = (v) => roundHalfUp(Number.isFinite(v) ? v : 0);

/** Normalise the store `is_surcharge` value to exactly "0" | "1" | "2" (anything else ⇒ "0"). */
export function normalizeSurchargeMode(v) {
  const s = String(v == null ? "" : v).trim();
  return s === SURCHARGE_NCA || s === SURCHARGE_DUAL ? s : SURCHARGE_OFF;
}

/** Engine config from register settings. Debit % applies in NCA mode only (Android setNonCashAdjustment). */
export function dualPriceConfig(settings) {
  const mode = normalizeSurchargeMode(settings && settings.isSurcharge);
  const pct = toNum(settings && settings.surchargePerCredit);
  return {
    mode,
    pct: pct > 0 ? pct : 0,
    debitPct: mode === SURCHARGE_NCA ? Math.max(toNum(settings && settings.surchargePerDebit), 0) : 0,
    debitSetting: Math.max(toNum(settings && settings.surchargePerDebit), 0), // raw setting (payload quirk)
  };
}

export const isDualPrice = (cfg) => !!cfg && cfg.mode === SURCHARGE_DUAL;
export const isNcaMode = (cfg) => !!cfg && cfg.mode === SURCHARGE_NCA;
export const dualActive = (cfg) => isDualPrice(cfg) && toNum(cfg.pct) > 0;
export const surchargeMultiplier = (cfg) => 1 + toNum(cfg && cfg.pct) / 100;

/** Android inflateCustomItemPrice / DatabaseRepository reprice: the card price of a base amount. */
export function inflatePrice(cfg, base) {
  return dualActive(cfg) ? r2(toNum(base) * surchargeMultiplier(cfg)) : r2(toNum(base));
}

/** Android toBasePrice: the cash price of a credit amount. */
export function toBasePrice(cfg, credit) {
  return dualActive(cfg) ? r2(toNum(credit) / surchargeMultiplier(cfg)) : r2(toNum(credit));
}

const isSoldGiftCard = (l) => !!l && !!l.isGiftCard && !l.isSplitPaymentDiscount;
const giftUnitFace = (l) => (toNum(l.orderUnitNewPrice) > 0 ? toNum(l.orderUnitNewPrice) : toNum(l.orderUnitPrice));

/** Android giftCardFaceTotal — sold gift cards are never inflated, so their face is carved out. */
export function giftCardFaceTotal(cfg, lines) {
  if (!isDualPrice(cfg)) return 0;
  return r2((lines || []).filter(isSoldGiftCard).reduce((s, l) => s + giftUnitFace(l) * toNum(l.orderCount || 1), 0));
}

/**
 * Derive the cash (base) and card columns from one credit-space recalculation.
 * @param {object} cfg dualPriceConfig
 * @param {object} p
 *   lines, subtotal (mSubTotal), couponDiscountValue (order-level discount total), taxRateInAmount,
 *   baseTaxRateInAmount, grandTotal (mGrandTotal, net of wallets), loyalty, storeCredit,
 *   lotteryScratcher (Σ payouts, positive), redeemedGiftCard, isSplit, taxLines [{amount}]
 */
export function computeDualTotals(cfg, p = {}) {
  const dual = isDualPrice(cfg);
  const M = surchargeMultiplier(cfg);
  const mSubTotal = toNum(p.subtotal);
  const coupon = toNum(p.couponDiscountValue);
  const creditTax = toNum(p.taxRateInAmount);
  const mGrandTotal = toNum(p.grandTotal);
  const lp = toNum(p.loyalty);
  const sc = toNum(p.storeCredit);
  const payouts = toNum(p.lotteryScratcher);
  const redeemed = toNum(p.redeemedGiftCard);

  const giftFace = giftCardFaceTotal(cfg, p.lines);
  const deduction = lp !== 0 || sc !== 0 || payouts !== 0 || redeemed !== 0;
  const frac = mSubTotal > 0 && coupon > 0 ? Math.min(Math.max(coupon / mSubTotal, 0), 1) : 0;

  const baseSub = r2(toBasePrice(cfg, mSubTotal - giftFace) + giftFace);
  const baseOrderDisc = coupon <= 0 ? 0 : r2(baseSub * frac);
  const baseTax = dual && lp !== 0 ? r2(toNum(p.baseTaxRateInAmount)) : r2(toBasePrice(cfg, creditTax));
  const exempt = dual ? r2(giftFace - payouts - redeemed - sc) : 0;

  let baseGrand;
  if (!dual) baseGrand = r2(mGrandTotal);
  else if (!deduction) baseGrand = r2(baseSub - baseOrderDisc + baseTax);
  else if (giftFace === 0) baseGrand = r2(baseSub - baseOrderDisc - lp - sc - payouts - redeemed + baseTax);
  else baseGrand = r2(toBasePrice(cfg, mGrandTotal - exempt) + exempt);

  const cardDerived = dual && !p.isSplit && !deduction;
  const cardSub = cardDerived ? r2((baseSub - giftFace) * M + giftFace) : r2(mSubTotal);
  const giftShare = giftFace * frac;
  const cardOrderDisc = cardDerived ? r2((baseOrderDisc - giftShare) * M + giftShare) : r2(coupon);
  const cardTax = cardDerived ? r2(baseTax * M) : r2(creditTax);
  const cardGrand = cardDerived ? r2(cardSub - cardOrderDisc + cardTax) : r2(mGrandTotal);
  const creditNca = dual ? r2(Math.max(0, baseSub - baseOrderDisc) * toNum(cfg.pct) / 100) : 0;

  return {
    dual,
    giftFace,
    hasDeduction: deduction,
    cardDerived,
    baseSubTotal: baseSub,
    baseOrderDiscount: baseOrderDisc,
    baseTax,
    baseGrandTotal: baseGrand,
    cardSubTotal: cardSub,
    cardOrderDiscount: cardOrderDisc,
    cardTax,
    cardGrandTotal: cardGrand,
    creditPriceNca: creditNca,
    savings: dual ? r2(cardGrand - baseGrand) : 0,
    cardTaxLines: perTaxSplit(cfg, p.taxLines, creditTax, cardTax),
    baseTaxLines: perTaxSplit(cfg, p.taxLines, creditTax, baseTax),
  };
}

/** Android perTaxValueString: scale each tax row to `target`; the LAST row absorbs the residual. */
export function perTaxSplit(cfg, taxLines, creditTotal, target) {
  const rows = Array.isArray(taxLines) ? taxLines : [];
  if (!isDualPrice(cfg) || toNum(creditTotal) <= 0 || rows.length === 0) return rows.map((t) => ({ ...t }));
  const ratio = toNum(target) / toNum(creditTotal);
  const scaled = rows.map((t) => r2(toNum(t.amount) * ratio));
  const residual = r2(toNum(target) - scaled.reduce((s, v) => s + v, 0));
  return rows.map((t, i) => ({ ...t, amount: i === rows.length - 1 ? r2(scaled[i] + residual) : scaled[i] }));
}

/** Android splitCardBaseRatio: card ÷ base for this order; M when base ≤ 0 or card < base. */
export function splitCardBaseRatio(cfg, dual) {
  const base = toNum(dual && dual.baseGrandTotal);
  const card = toNum(dual && dual.cardGrandTotal);
  return base <= 0 || card < base ? surchargeMultiplier(cfg) : card / base;
}

/**
 * Android register summary (IPF:6474-6535): Subtotal/Discount/Tax rows and the Pay amount.
 * Pay = abs(!split ? getCardGrandTotal : dual ? remaining base × ratio : remaining).
 */
export function registerDisplay(cfg, { subtotal, orderDiscount, taxLines, grandTotal, customerOwes, isSplit, dual }) {
  const d = dual && dual.dual ? dual : null;
  const pay = !isSplit ? (d ? d.cardGrandTotal : grandTotal) : d ? r2(toNum(customerOwes) * splitCardBaseRatio(cfg, d)) : customerOwes;
  return {
    subtotal: d ? d.cardSubTotal : r2(subtotal),
    orderDiscount: d ? d.cardOrderDiscount : r2(orderDiscount),
    taxLines: d ? d.cardTaxLines : taxLines,
    total: d ? d.cardGrandTotal : r2(grandTotal),
    payAmount: Math.abs(r2(toNum(pay))),
  };
}

/**
 * Dual-price CREDIT tender (Android getAmountWithNca + getCardAmount/getCardNca). The web balance is
 * the CASH remainder; `typed` is the amount box.
 *  - full, first tender      → charge getCardGrandTotal, base = cash total
 *  - full, mid-split         → charge remaining base × splitCardBaseRatio
 *  - typed partial           → charged VERBATIM in card dollars; base = typed ÷ ratio
 * The base pays down the balance; nca = charge − base is the card premium.
 */
export function dualCardCharge(cfg, { dual, balanceDue, typed, isSplit }) {
  const due = r2(balanceDue);
  const t = r2(typed);
  const ratio = splitCardBaseRatio(cfg, dual);
  const fullCharge = !isSplit ? r2(toNum(dual && dual.cardGrandTotal)) : r2(due * ratio);
  let charge;
  let base;
  // The box is pre-filled with the CASH remainder: that (or anything ≥ the card remainder) is the full
  // tender; any other typed amount — including one between the cash and card totals — is a partial
  // charged verbatim (Android isTenderAmountTypedByUser).
  if (t <= 0 || t === due || t >= fullCharge) {
    base = due;
    charge = fullCharge;
  } else {
    charge = t;
    base = Math.min(r2(t / ratio), due);
  }
  return { charge, base, nca: Math.max(r2(charge - base), 0), percent: toNum(cfg && cfg.pct) };
}
